import { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { Send } from 'lucide-react'
import { useLeadComments, useCreateLeadComment } from '../../api/leads'
import { formatDate } from '../../lib/utils'

interface LeadCommentsProps {
  leadId: string
}

export default function LeadComments({ leadId }: LeadCommentsProps) {
  const { data: comments, isLoading } = useLeadComments(leadId)
  const createComment = useCreateLeadComment()
  const [content, setContent] = useState('')
  const scrollRef = useRef<HTMLDivElement>(null)

  // Scroll to bottom when new comments arrive
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [comments])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = content.trim()
    if (!trimmed) return

    createComment.mutate(
      { leadId, content: trimmed },
      { onSuccess: () => setContent('') }
    )
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e)
    }
  }

  return (
    <div className="flex flex-col h-full">
      <label className="block text-xs font-medium text-text-tertiary uppercase tracking-wider mb-2">
        Комментарии
      </label>

      {/* Comments feed */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto space-y-3 mb-3 max-h-60 min-h-[80px]"
      >
        {isLoading ? (
          <div className="flex items-center justify-center py-6">
            <div className="animate-spin w-5 h-5 border-2 border-accent-lime border-t-transparent rounded-full" />
          </div>
        ) : !comments || comments.length === 0 ? (
          <div className="text-sm text-text-tertiary text-center py-6">
            Нет комментариев
          </div>
        ) : (
          <AnimatePresence initial={false}>
            {comments.map((comment) => (
              <motion.div
                key={comment.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2 }}
                className="bg-white/5 rounded-lg p-3"
              >
                <div className="flex items-start gap-2.5">
                  {/* Avatar */}
                  <div className="w-7 h-7 rounded-full bg-accent-blue/20 text-accent-blue text-xs font-medium flex items-center justify-center flex-shrink-0 mt-0.5">
                    {comment.user?.username?.charAt(0).toUpperCase() ?? '?'}
                  </div>

                  <div className="flex-1 min-w-0">
                    {/* Header: username + timestamp */}
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium text-text-primary truncate">
                        {comment.user?.username ?? 'Удалённый пользователь'}
                      </span>
                      <span className="text-xs text-text-tertiary flex-shrink-0">
                        {formatDate(comment.createdAt, {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </div>

                    {/* Content */}
                    <p className="text-sm text-text-primary whitespace-pre-wrap break-words">
                      {comment.content}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        )}
      </div>

      {/* Input area */}
      <form onSubmit={handleSubmit} className="flex items-end gap-2">
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Написать комментарий..."
          rows={1}
          className="flex-1 px-3 py-2 bg-bg-tertiary border border-border-medium rounded-lg text-sm text-text-primary placeholder-text-tertiary focus:outline-none focus:border-accent-lime/50 focus:ring-1 focus:ring-accent-lime/20 resize-none"
        />
        <button
          type="submit"
          disabled={!content.trim() || createComment.isPending}
          className="w-9 h-9 flex items-center justify-center rounded-lg bg-accent-lime/15 text-accent-lime hover:bg-accent-lime/25 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0"
        >
          {createComment.isPending ? (
            <div className="animate-spin w-4 h-4 border-2 border-accent-lime border-t-transparent rounded-full" />
          ) : (
            <Send className="w-4 h-4" />
          )}
        </button>
      </form>
    </div>
  )
}
